/**
 * Service for handling approval review operations
 * Provides business logic for determining button visibility based on selected files
 */

export interface SelectedFileInfo {
  fileImportId: string;
  uploadType: number;
  fileName: string;
}

export interface ButtonVisibility {
  showApprove: boolean;
  showReject: boolean;
  showSendToAdmin: boolean;
  showDataValidation: boolean;
  showAddNote: boolean;
  showEditForm: boolean;
}

const isIndividualForm = (uploadType: number): boolean =>
  (uploadType >= 2 && uploadType <= 10) || uploadType === 12 || uploadType === 15;

/**
 * Determines which buttons should be displayed based on selected upload types
 *
 * Business Rules:
 * - No selection: Show Data Validation, Add Note, Reject, Approve (default view)
 * - Single file (types 2-10): Edit Form, Reject, Send To Admin
 * - Single file (types 1, 11, or 13): Data Validation, Add Note, Reject, Approve
 * - Multiple files: Reject is unavailable because every rejection requires its own reason and note
 */
export const determineVisibleButtons = (selectedFiles: SelectedFileInfo[]): ButtonVisibility => {
  // No selection - show default buttons (Data Validation, Add Note, Reject, Approve)
  if (selectedFiles.length === 0) {
    return {
      showApprove: true,
      showReject: true,
      showSendToAdmin: false,
      showDataValidation: true,
      showAddNote: true,
      showEditForm: false,
    };
  }

  // Categorize files by upload type
  const hasIndividualForms = selectedFiles.some((file) => isIndividualForm(file.uploadType));
  const hasWithholdingOrFormPack = selectedFiles.some(
    (file) => file.uploadType === 1 || file.uploadType === 11 || file.uploadType === 13
  );

  // Single file selection
  if (selectedFiles.length === 1) {
    const file = selectedFiles[0];

    // Individual forms (2-10): Show Edit Form, Reject, Send To Admin
    if (isIndividualForm(file.uploadType)) {
      return {
        showApprove: false,
        showReject: true,
        showSendToAdmin: true,
        showDataValidation: false,
        showAddNote: false,
        showEditForm: true,
      };
    }

    // Withholding/FormPack/Deloitte Excel (1, 11, or 13): Show Data Validation, Add Note, Reject, Approve
    if (file.uploadType === 1 || file.uploadType === 11 || file.uploadType === 13) {
      return {
        showApprove: true,
        showReject: true,
        showSendToAdmin: false,
        showDataValidation: true,
        showAddNote: true,
        showEditForm: false,
      };
    }
  }

  // Multiple files selected
  // Rejection is limited to one file so each rejection can include its own reason and note.
  if (hasIndividualForms && hasWithholdingOrFormPack) {
    return {
      showApprove: false,
      showReject: false,
      showSendToAdmin: false,
      showDataValidation: false,
      showAddNote: false,
      showEditForm: false,
    };
  }

  // If only individual forms (2-10) are selected: Send To Admin
  if (hasIndividualForms) {
    return {
      showApprove: false,
      showReject: false,
      showSendToAdmin: true,
      showDataValidation: false,
      showAddNote: false,
      showEditForm: false,
    };
  }

  // If only withholding/formpack (1 or 11) are selected: Approve
  if (hasWithholdingOrFormPack) {
    return {
      showApprove: true,
      showReject: false,
      showSendToAdmin: false,
      showDataValidation: false,
      showAddNote: false,
      showEditForm: false,
    };
  }

  // Default: hide all buttons
  return {
    showApprove: false,
    showReject: false,
    showSendToAdmin: false,
    showDataValidation: false,
    showAddNote: false,
    showEditForm: false,
  };
};

/**
 * Gets display text for selected files count
 */
export const getSelectionCountText = (count: number): string => {
  if (count === 0) return "No files selected";
  if (count === 1) return "1 file selected";
  return `${count} files selected`;
};

/**
 * Validates if data validation can be performed (single selection with type 1, 11, or 13 only)
 */
export const canPerformDataValidation = (selectedFiles: SelectedFileInfo[]): boolean => {
  return (
    selectedFiles.length === 1 &&
    (selectedFiles[0].uploadType === 1 ||
      selectedFiles[0].uploadType === 11 ||
      selectedFiles[0].uploadType === 13)
  );
};

/**
 * Validates if add note can be performed (single selection only)
 */
export const canAddNote = (selectedFiles: SelectedFileInfo[]): boolean => {
  return selectedFiles.length === 1;
};

/**
 * Validates if edit form can be performed (single selection with types 2-10 only)
 */
export const canEditForm = (selectedFiles: SelectedFileInfo[]): boolean => {
  return selectedFiles.length === 1 && isIndividualForm(selectedFiles[0].uploadType);
};

export const entityTypeMap = {
  Partnership: "Partnership",
  "Simple trust": "Simple Trust",
  "Central Bank of Issue": "Central Bank of Issue",
  "Grantor trust": "Grantor Trust",
  "Tax-exempt organization": "Tax-Exempt Organization",
  "Foreign government": "Foreign Government",
  "Foreign central bank of issue": "Foreign Central Bank of Issue",
  "Foreign tax-exempt organization": "Foreign Tax-Exempt Organization",
  "Foreign private foundation": "Foreign Private Foundation",
  "Government of a U.S. territory": "Government of a U.S. territory",
  "Withholding qualified holder under section 1445":
    "Withholding qualified holder under section 1445",
  "Private foundation": "Private Foundation",
  "Disregarded entity": "Disregarded Entity",
  Corporation: "Corporation",
  "Complex trust": "Complex Trust",
  Estate: "Estate",
  "International organization": "International Organization",
  "Foreign Government - Controlled Entity": "Foreign Government - Controlled Entity",
  "Foreign Government - Integral Part": "Foreign Government - Integral Part",

  "Hybrid making a treaty claim (disregarded entity, partnership, simple trust, or grantor trust)":
    "Hybrid entity making Treaty Claim",

  "IMY QI": "Qualified Intermediary",
  "IMY Nonqualified intermediary": "Non Qualified Intermediary",
  "IMY Territory financial institution": "Territory Financial Institution",
  "IMY U.S. branch": "U.S. Branch",
  "IMY Withholding foreign partnership": "Withholding Foreign Partnership",
  "IMY Withholding foreign trust": "Withholding Foreign Trust",
  "IMY Nonwithholding foreign partnership": "Non-withholding Foreign Partnership",
  "IMY Nonwithholding foreign simple trust": "Non-withholding Foreign Simple Trust",
  "IMY Nonwithholding foreign grantor trust": "Non-withholding Foreign Grantor Trust",
  EXP_Foreign_government: "Foreign Government",
  EXP_International_organization: "International Organization",
  EXP_Foreign_central_bank_issue: "Foreign Central Bank of Issue",
  EXP_Foreign_tax_exempt_organization: "Foreign Tax-Exempt Organization",
  EXP_Foreign_private_foundation: "Foreign Private Foundation",
  EXP_Government_of_us_territory: "Government of a U.S. territory",
  EXP_Withholding_qualified_holder: "Withholding qualified holder under section 1445",

  "Individual/sole proprietor": "Individual/Sole Proprietor",
  "C corporation": "C-Corporation",
  "S corporation": "S-Corporation",
  "Trust/estate": "Trust/Estate",
  Other: "Other",
  Individual: "Individual",
  "Limited Liability Company": "LLC",

  "BENE-4-Foreign_Government - Integral_Part": "Foreign Government - Integral Part",
  "BENE-4-Foreign_Government_Controlled_Entity": "Foreign Government - Controlled Entity",
  "BENE-4-International_organization": "International Organization",
  "BENE-4-Tax-exempt_organization": "Tax-Exempt Organization",
  "BENE-4-central_bank": "Central Bank of Issue",
  "BENE-4-complex_trust": "Complex Trust",
  "BENE-4-corporation": "Corporation",
  "BENE-4-disregarded_entity": "Disregarded Entity",
  "BENE-4-estate": "Estate",
  "BENE-4-grantor_trust": "Grantor Trust",
  "BENE-4-hybrid_no": "Hybrid entity making Treaty Claim",
  "BENE-4-hybrid_yes": "Hybrid entity making Treaty Claim",
  "BENE-4-partnership": "Partnership",
  "BENE-4-private_foundation": "Private Foundation",
  "BENE-4-simple_trust": "Simple Trust",

  ECI_Central_bank_of_issue: "Central Bank of Issue",
  ECI_International_organization: "International Organization",
  ECI_Tax_exempt_organization: "Tax-Exempt Organization",
  ECI_Complex_trust: "Complex Trust",
  ECI_Corporation: "Corporation",
  ECI_Estate: "Estate",
  ECI_Government_controlled_entity: "Foreign Government - Controlled Entity",
  ECI_Government_integral: "Foreign Government - Integral Part",
  ECI_Grantor_trust: "Grantor Trust",
  ECI_Individual: "Individual",
  ECI_Partnership: "Partnership",
  ECI_Private_foundation: "Private Foundation",
  ECI_Simple_trust: "Simple Trust",

  "IMY-4-QI": "Qualified Intermediary",
  "IMY-4-nonqualified_intermediary": "Non Qualified Intermediary",
  "IMY-4-nonwithholding_foreign_grantor_trust": "Non-withholding Foreign Grantor Trust",
  "IMY-4-nonwithholding_foreign_partnership": "Non-withholding Foreign Partnership",
  "IMY-4-nonwithholding_foreign_simple_trust": "Non-withholding Foreign Simple Trust",
  "IMY-4-territory_financial_institution": "Territory Financial Institution",
  "IMY-4-us_branch": "U.S. Branch",
  "IMY-4-withholding_foreign_partnership": "Withholding Foreign Partnership",
  "IMY-4-withholding_foreign_trust": "Withholding Foreign Trust",

  W9_Tax_classification_c_corporation: "C-Corporation",
  W9_Tax_classification_individual_sole_proprietor: "Individual/Sole Proprietor",
  W9_Tax_classification_llc: "LLC",
  W9_Tax_classification_partnership: "Partnership",
  W9_Tax_classification_other: "Other",
  W9_Tax_classification_s_corporation: "S-Corporation",
  W9_Tax_classification_trust_estate: "Trust/Estate",
};

export const chapter4StatusMap = {
  "Nonparticipating FFI": "Limited Branch treated as Nonparticipating FFI",
  "Participating FFI": "Participating FFI - Other",
  "Reporting Model 1 FFI": "Reporting Model 1 FFI",
  "Reporting Model 2 FFI": "Reporting Model 2 FFI",
  "Sponsored FFI. Complete Part IV": "Sponsored FFI",

  "Certified deemed-compliant nonregistering local bank. Complete Part V":
    "Certified Deemed-Compliant FFI - Other",
  "Certified deemed-compliant FFI with only low-value accounts. Complete Part VI":
    "Certified deemed-compliant FFI with only low-value accounts",
  "Certified deemed-compliant sponsored, closely held investment vehicle. Complete Part VII":
    "Certified Deemed-Compliant FFI - Sponsored Entity",
  "Certified deemed-compliant limited life debt investment entity. Complete Part VIII":
    "Certified deemed-compliant limited life debt investment entity",

  "Certain investment entities that do not maintain financial accounts. Complete Part IX":
    "Certain investment entities that do not maintain financial accounts",
  "Owner-documented FFI. Complete Part X": "Other",
  "Restricted distributor. Complete Part XI": "Other",
  "Nonreporting IGA FFI. Complete Part XII": "Nonreporting IGA FFI",
  "Foreign government, government of a U.S. possession, or foreign central bank of issue. Complete Part XIII":
    "Foreign government, government of a U.S. possession, or foreign central bank of issue",
  "International organization. Complete Part XIV": "International organization",

  "Exempt retirement plans. Complete Part XV": "Exempt Beneficial Owner",
  "Entity wholly owned by exempt beneficial owners. Complete Part XVI":
    "Entity wholly owned by exempt beneficial owners",
  "Territory financial institution. Complete Part XVII": "Territory FI - treated as U.S. person",

  "Excepted nonfinancial group entity. Complete Part XVIII": "Excepted nonfinancial group entity",
  "Excepted nonfinancial start-up company. Complete Part XIX":
    "Excepted nonfinancial start-up company",
  "Excepted nonfinancial entity in liquidation or bankruptcy. Complete Part XX": "Other",

  "501(c) organization. Complete Part XXI": "501(c) organization",
  "Nonprofit organization. Complete Part XXII": "Nonprofit organization",
  "Publicly traded NFFE or NFFE affiliate of a publicly traded corporation. Complete Part XXIII":
    "Publicly traded NFFE or NFFE affiliate of a publicly traded corporation",
  "Excepted territory NFFE. Complete Part XXIV": "Other",
  "Active NFFE. Complete Part XXV": "Active NFFE",
  "Passive NFFE. Complete Part XXVI": "Passive NFFE",
  "Excepted inter-affiliate FFI. Complete Part XXVII": "Other",

  "Direct reporting NFFE": "Direct reporting NFE",
  "Sponsored direct reporting NFFE. Complete Part XXVIII": "Sponsored direct reporting NFFE",

  "Account that is not a financial account": "Other",
  "Registered deemed-compliant FFI": "Registered deemed-compliant FFI",
  "Nonreporting IGA FFI. Complete Part III": "Nonreporting IGA FFI",
  "Territory financial institution. Complete Part III": "Territory FI - not treated as U.S. person",
  "Chapter 4 International organization": "International organization",
  "Chapter 4 Foreign government":
    "Foreign government, government of a U.S. possession, or foreign central bank of issue",
  "Exempt retirement plan of foreign government. Complete Part III": "Exempt Beneficial Owner",

  "IMY Registered deemed-compliant FFI Part XIX": "Registered deemed-compliant FFI",
  "IMY CH-4 Territory financial institution. Complete Part V":
    "Territory FI - not treated as U.S. person",
  "IMY Sponsored FFI. Complete Part X": "Sponsored FFI",
  "IMY Certified deemed-compliant nonregistering local bank. Complete Part XII":
    "Certified Deemed-Compliant FFI - Other",
  "IMY Certified deemed-compliant FFI with only low-value accounts. Complete Part XIII":
    "Certified deemed-compliant FFI with only low-value accounts",
  "IMY Certified deemed-compliant sponsored, closely held investment vehicle. Complete Part XIV":
    "Certified Deemed-Compliant FFI - Sponsored Entity",
  "IMY Certified deemed-compliant limited life debt investment entity. Complete Part XV":
    "Certified deemed-compliant limited life debt investment entity",
  "IMY Certain investment entities that do not maintain financial accounts. Complete Part XVI":
    "Certain investment entities that do not maintain financial accounts",
  "IMY Owner-documented FFI. Complete Part XI": "Other",
  "IMY Foreign central bank of issue. Complete Part XVIII": "Foreign central bank of issue",
  "IMY Nonreporting IGA FFI. Complete Part XIX": "Nonreporting IGA FFI",
  "IMY Exempt retirement plans. Complete Part XX": "Exempt Beneficial Owner",
  "IMY Excepted nonfinancial group entity. Complete Part XXI": "Excepted nonfinancial group entity",
  "IMY Excepted nonfinancial start-up company. Complete Part XXII":
    "Excepted nonfinancial start-up company",
  "IMY Excepted nonfinancial entity in liquidation or bankruptcy. Complete Part XXIII": "Other",
  "IMY Publicly traded NFFE or NFFE affiliate of a publicly traded corporation. Complete Part XXIV":
    "Publicly traded NFFE or NFFE affiliate of a publicly traded corporation",
  "IMY Excepted territory NFFE. Complete Part XXV": "Other",
  "IMY Active NFFE. Complete Part XXVI": "Active NFFE",
  "IMY Passive NFFE. Complete Part XXVII": "Passive NFFE",
  "IMY Direct reporting NFFE": "Direct reporting NFE",
  "IMY Sponsored direct reporting NFFE. Complete Part XXVIII": "Sponsored direct reporting NFFE",
  "Active NFFE with Controlling Persons": "Active NFFE with Controlling Persons",
  "Passive NFFE with Controlling Persons": "Passive NFFE with Controlling Persons",
  "Sponsored Investment Entity": "Sponsored Investment Entity",
  "Sponsored Controlled Foreign Corporation": "Sponsored Controlled Foreign Corporation",
  "Non-Reporting IGA FFI with Controlling Persons":
    "Non-Reporting IGA FFI with Controlling Persons",
  "Reporting Model 1 FFI with Controlling Persons":
    "Reporting Model 1 FFI with Controlling Persons",
  "Reporting Model 2 FFI with Controlling Persons":
    "Reporting Model 2 FFI with Controlling Persons",
  "Registered Deemed-Compliant FFI with Controlling Persons":
    "Registered Deemed-Compliant FFI with Controlling Persons",
  "Certified Deemed-Compliant FFI with Controlling Persons":
    "Certified Deemed-Compliant FFI with Controlling Persons",
  "Restricted Fund with Controlling Persons": "Restricted Fund with Controlling Persons",
  "Exempt Beneficial Owner with Controlling Persons":
    "Exempt Beneficial Owner with Controlling Persons",
  "Excepted NFFE with Controlling Persons": "Excepted NFFE with Controlling Persons",
  "Excepted Territory NFFE with Controlling Persons":
    "Excepted Territory NFFE with Controlling Persons",

  EXP_Direct_reporting_NFFE: "Direct Reporting NFFE",
  EXP_Exempt_retirement_plan: "Exempt Retirement Plan",
  EXP_4_foreign_government_central_bank: "Foreign Government",
  EXP_International_organization: "International Organization",
  EXP_Nonreporting_IGA_FFI: "Nonreporting IGA FFI",
  EXP_Participating_FFI: "Participating FFI",
  EXP_Passive_NFFE: "Passive NFFE",
  EXP_Registered_deemed_compliant_FFI: "Registered Deemed-Compliant FFI",
  EXP_Reporting_Model1_FFI: "Reporting Model 1 FFI",
  EXP_Reporting_Model2_FFI: "Reporting Model 2 FFI",
  EXP_Sponsored_direct_reporting_NFFE: "Sponsored Direct Reporting NFFE",
  EXP_Territory_financial_institution: "Territory Financial Institution",
  EXP_501c_organization: "501(c) organization",

  "BENE-5-5_501c": "501(c) organization",
  "BENE-5-International_organization": "International organization",
  "BENE-5-account_not_financial": "Other",
  "BENE-5-active_nffe": "Active NFFE",
  "BENE-5-certified_limited_life": "Certified deemed-compliant limited life debt investment entity",
  "BENE-5-certified_local_bank": "Certified Deemed-Compliant FFI - Other",
  "BENE-5-certified_low_value": "Certified deemed-compliant FFI with only low-value accounts",
  "BENE-5-certified_sponsored": "Certified Deemed-Compliant FFI - Sponsored Entity",
  "BENE-5-direct_reporting": "Direct reporting NFE",
  "BENE-5-entity_exempt_owners": "Entity wholly owned by exempt beneficial owners",
  "BENE-5-excepted_affiliate": "Other",
  "BENE-5-excepted_group": "Excepted nonfinancial group entity",
  "BENE-5-excepted_liquidation": "Other",
  "BENE-5-excepted_startup": "Excepted nonfinancial start-up company",
  "BENE-5-excepted_territory": "Other",
  "BENE-5-exempt_retirement": "Exempt Beneficial Owner",
  "BENE-5-foreign_government":
    "Foreign government, government of a U.S. possession, or foreign central bank of issue",
  "BENE-5-investment_entities":
    "Certain investment entities that do not maintain financial accounts",
  "BENE-5-nonparticipating_ffi": "Limited Branch treated as Nonparticipating FFI",
  "BENE-5-nonprofit": "Nonprofit organization",
  "BENE-5-nonreporting_iga": "Nonreporting IGA FFI",
  "BENE-5-owner_documented": "Other",
  "BENE-5-participating_ffi": "Participating FFI - Other",
  "BENE-5-passive_nffe": "Passive NFFE",
  "BENE-5-publicly_traded":
    "Publicly traded NFFE or NFFE affiliate of a publicly traded corporation",
  "BENE-5-registered_deemed": "Registered deemed-compliant FFI",
  "BENE-5-reporting_model1": "Reporting Model 1 FFI",
  "BENE-5-reporting_model2": "Reporting Model 2 FFI",
  "BENE-5-restricted_distributor": "Other",
  "BENE-5-sponsored_direct": "Sponsored direct reporting NFFE",
  "BENE-5-sponsored_ffi": "Sponsored FFI",
  "BENE-5-territory_financial": "Territory FI - treated as U.S. person",

  "IMY-active_nffe": "Active NFFE",
  "IMY-certain_investment_entities":
    "Certain investment entities that do not maintain financial accounts",
  "IMY-certified_deemed_debt_entity":
    "Certified deemed-compliant limited life debt investment entity",
  "IMY-certified_deemed_investment_vehicle": "Certified Deemed-Compliant FFI - Sponsored Entity",
  "IMY-certified_deemed_local_bank": "Certified Deemed-Compliant FFI - Other",
  "IMY-certified_deemed_low_value": "Certified deemed-compliant FFI with only low-value accounts",
  "IMY-direct_reporting_nffe": "Direct reporting NFE",
  "IMY-excepted_nonfinancial_group": "Excepted nonfinancial group entity",
  "IMY-excepted_nonfinancial_liquidation": "Other",
  "IMY-excepted_nonfinancial_startup": "Excepted nonfinancial start-up company",
  "IMY-excepted_territory_nffe": "Other",
  "IMY-exempt_retirement_plans": "Exempt Beneficial Owner",
  "IMY-foreign_central_bank": "Foreign central bank of issue",
  "IMY-nonparticipating_ffi": "Limited Branch treated as Nonparticipating FFI",
  "IMY-nonreporting_iga_ffi": "Nonreporting IGA FFI",
  "IMY-owner_documented_ffi": "Other",
  "IMY-participating_ffi": "Participating FFI - Other",
  "IMY-passive_nffe": "Passive NFFE",
  "IMY-publicly_traded_nffe":
    "Publicly traded NFFE or NFFE affiliate of a publicly traded corporation",
  "IMY-registered_deemed_compliant_ffi": "Registered deemed-compliant FFI",
  "IMY-reporting_model1_ffi": "Reporting Model 1 FFI",
  "IMY-reporting_model2_ffi": "Reporting Model 2 FFI",
  "IMY-restricted_distributor": "Other",
  "IMY-sponsored_direct_reporting_nffe": "Sponsored direct reporting NFFE",
  "IMY-sponsored_ffi": "Sponsored FFI",
  "IMY-territory_financial_institution": "Territory FI - not treated as U.S. person",
};
